import { Stack } from '@atlaskit/primitives';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import {
  DatabaseFieldType,
  type DatabaseField,
  type DatabaseValue
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  type DatabaseDraft,
  type DatabaseChange,
  type ValueChange
} from '@databases/utils/database/changesets';
import { isEmptyValue } from '@databases/utils/database/field-types';
import { getDefaultValue } from '@databases/utils/database/utils';
import React, { type FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { getDefaultValueModeSelectConfig } from './DefaultValueModeSelectConfig.ts';
import DefaultValuePicker from './DefaultValuePicker.tsx';
import DefaultValueInput from './shared/DefaultValueInput.tsx';
import DefaultValueModeSelect, {
  type DefaultValueMode
} from './shared/DefaultValueModeSelect.tsx';

type DefaultValueSettingProps = {
  contentId: string;
  database: DatabaseDraft;
  field: DatabaseField;
};

const DefaultValueSetting: FunctionComponent<DefaultValueSettingProps> = ({
  contentId,
  database,
  field
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const store = defaultStoreUtils.useStore();
  const { formatMessage } = useIntl();
  const defaultValue = getDefaultValue(database, field);
  const [optimisticDefaultValue, setOptimisticDefaultValue] =
    useState<DatabaseValue | null>(null);
  const [optimisticDefaultValueMode, setOptimisticDefaultValueMode] =
    useState<DefaultValueMode | null>(null);
  const effectiveDefaultValue = optimisticDefaultValue ?? defaultValue;
  const effectiveDefaultValueIsEmpty = defaultStoreUtils.useEqualSelector((state) =>
    isEmptyValue({ database, field, value: effectiveDefaultValue }, state)
  );
  const hasValue =
    !effectiveDefaultValueIsEmpty &&
    (field.settings.defaultEnabled || optimisticDefaultValue !== null);

  useEffect(() => {
    setOptimisticDefaultValue(null);
    setOptimisticDefaultValueMode(null);
  }, [field.id]);

  const handleAddChangeToDraft = useCallback(
    (change: DatabaseChange) => {
      dispatch(addChangeToDraft(contentId, change));
    },
    [contentId, dispatch]
  );

  const handleChange = useCallback(
    (value: DatabaseValue, change: ValueChange | null) => {
      setOptimisticDefaultValue(value);
      const valueIsEmpty = isEmptyValue({ database, field, value }, store.getState());

      if (!field.settings.defaultEnabled && !valueIsEmpty) {
        dispatch(
          addChangeToDraft(contentId, {
            type: DatabaseChangeType.SET_DEFAULT_ENABLED,
            fieldId: field.id,
            defaultEnabled: true
          })
        );
      }

      if (field.settings.defaultEnabled && valueIsEmpty) {
        dispatch(
          addChangeToDraft(contentId, {
            type: DatabaseChangeType.SET_DEFAULT_ENABLED,
            fieldId: field.id,
            defaultEnabled: false
          })
        );
      }

      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.BULK_SET_UNDEFINED_VALUES,
          fieldId: field.id,
          value,
          change
        })
      );
    },
    [contentId, database, dispatch, field, store]
  );

  const defaultValueModeSelectConfig = getDefaultValueModeSelectConfig({
    addChangeToDraft: handleAddChangeToDraft,
    effectiveDefaultValue,
    field,
    formatMessage,
    optimisticDefaultValueMode,
    setOptimisticDefaultValue,
    setOptimisticDefaultValueMode
  });
  const shouldRenderPicker =
    defaultValueModeSelectConfig === null ||
    (defaultValueModeSelectConfig.options.find(
      (option) => option.value === defaultValueModeSelectConfig.value
    )?.shouldRenderPicker ??
      false);

  return (
    <DefaultValueInput
      hasValue={hasValue}
      shouldForwardLabelClick={field.type !== DatabaseFieldType.DATE}
      testId="field-settings.default-value"
    >
      {(slotProps) => (
        <Stack space="space.075">
          {defaultValueModeSelectConfig && (
            <DefaultValueModeSelect
              ariaDescribedBy={slotProps.ariaDescribedBy}
              ariaLabel={slotProps.ariaLabel}
              inputId={slotProps.inputId}
              onChange={defaultValueModeSelectConfig.onChange}
              options={defaultValueModeSelectConfig.options}
              placeholder={
                defaultValueModeSelectConfig.placeholder ?? slotProps.placeholder
              }
              value={defaultValueModeSelectConfig.value}
            />
          )}
          {shouldRenderPicker && (
            <DefaultValuePicker
              contentId={contentId}
              field={field}
              hasValue={hasValue}
              value={effectiveDefaultValue}
              onChange={handleChange}
              addChangeToDraft={handleAddChangeToDraft}
              slotProps={slotProps}
            />
          )}
        </Stack>
      )}
    </DefaultValueInput>
  );
};

export default DefaultValueSetting;
